import type { GridNewDeletedSocketResponseType } from '../grid/reduxStore/gridBlockSocketActions.ts';
import { deleteNewBlockWithSocket } from '../grid/reduxStore/gridBlockSocketActions.ts';
import { deleteGridBlockState, selectSectionById, setMaxHeightPage } from '../grid/reduxStore/editorSlice.ts';
import { setSaveStatus, setSaveStatusBasedOnApiResponse } from '../grid/reduxStore/saveStatusSlice.ts';
import { rootStore, useAppDispatch } from '../grid/reduxStore/Store.ts';
import { useCurrentUser } from '../../../providers/UserProvider.tsx';
import { useSocketClient } from '../../../providers/SocketContext.tsx';
import { DocumentSaveStatus } from '../shared/models/DocumentSaveStatus.ts';
import { EditorDefaultSidePanelType, setActiveSidePanel } from '../grid/reduxStore/blockStyleSettingsSlice.ts';
import { useSignaturesContext } from '../providers/SignaturesProvider.tsx';
import { calculatePageMaxHeight } from '../GridDndEditor/gridHelper.tsx';
import { AcknowledgmentResponseStatus, ErrorCode } from '../../../services/socket/SocketEvents.tsx';
import { useCallback } from 'react';
import { UndoRedoManagerCommandVersion } from '../UndoRedo/UndoRedoManagerCommandVersion.ts';
import { DeleteBlockCommand } from '../UndoRedo/DeleteBlockCommand.ts';
import { useCheckDocumentLockStatus } from 'components/editor/hooks/useCheckDocumentLockStatus';

export function useGridBlockDeletedHandlerWithoutUndoRedo() {
  const dispatch = useAppDispatch();
  const { data } = useCurrentUser();
  const userId = data.id;
  const { socketClient } = useSocketClient();
  const { getSignaturesBySectionId } = useSignaturesContext();

  const handleDeleteSuccess = useCallback(
    (
      sectionId: string,
      blockId: string,
      status: GridNewDeletedSocketResponseType['status'],
      errorCode: GridNewDeletedSocketResponseType['errorCode']
    ) => {
      dispatch(setSaveStatusBasedOnApiResponse({ status, errorCode }));
      dispatch(deleteGridBlockState({ sectionId, blockId }));
      dispatch(
        setActiveSidePanel({
          type: EditorDefaultSidePanelType.EDITOR_DEFAULT_SIDE_PANEL,
        })
      );

      const state = rootStore.getState();
      const section = selectSectionById(state, sectionId);
      const blocksContent = section?.blocksContent || {};

      const updatedBlocksContent = { ...blocksContent };
      delete updatedBlocksContent[blockId];

      const signatures = getSignaturesBySectionId(sectionId);
      const maxHeightPage = calculatePageMaxHeight(updatedBlocksContent, signatures);

      dispatch(setMaxHeightPage({ maxHeightPage, sectionId }));
    },
    [dispatch, getSignaturesBySectionId]
  );

  return useCallback(
    async (sectionId: string, blockId: string): Promise<GridNewDeletedSocketResponseType> => {
      dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVING }));

      return dispatch(
        deleteNewBlockWithSocket({
          blockId,
          socketClient,
          userId,
          callback: ({ status, errorCode }) => handleDeleteSuccess(sectionId, blockId, status, errorCode),
        })
      ).unwrap();
    },
    [dispatch, socketClient, userId, handleDeleteSuccess]
  );
}

export function useNewBlockDeletedHandler() {
  const dispatch = useAppDispatch();
  const { data } = useCurrentUser();
  const userId = data.id;
  const { socketClient } = useSocketClient();
  const { getSignaturesBySectionId } = useSignaturesContext();
  const checkDocumentLockStatus = useCheckDocumentLockStatus();

  return useCallback(
    async (sectionId: string, blockId: string): Promise<GridNewDeletedSocketResponseType> => {
      const undoRedoManager = UndoRedoManagerCommandVersion.getUndoRedoManager();
      const currentState = rootStore.getState();

      let command: DeleteBlockCommand;
      try {
        command = new DeleteBlockCommand(blockId, sectionId, {
          dispatch,
          socketClient,
          userId,
          checkDocumentLockStatus,
          getSignaturesBySectionId,
          currentState,
        });
      } catch (error) {
        console.error('Failed to create delete command:', error);
        throw error;
      }

      const success = await command.redo();

      if (success) {
        undoRedoManager.pushUndoRedoCommands(command);
      }

      return {
        status: success ? AcknowledgmentResponseStatus.OK : AcknowledgmentResponseStatus.FAILURE,
        ...(AcknowledgmentResponseStatus.FAILURE && { errorCode: ErrorCode.LOCKED }),
      };
    },
    [dispatch, socketClient, userId, getSignaturesBySectionId, checkDocumentLockStatus]
  );
}
